import CampaignApiClient from './campaignApiClient';
import { campaignDebug, campaignWarn } from './diagnostics';
import { parseClaimResponse, parseEvaluateResponse } from './parsing';
import { CampaignOutcome, CampaignRenderState, CampaignTrigger, EvaluationCandidate, PageContext } from './types';

/** REQ-CWC-01 — "debounced to at most one call per second". */
const EVALUATE_MIN_INTERVAL_MS = 1000;

type RenderStateSink = (state: CampaignRenderState) => void;

/**
 * IMBEE-8954 (T114 §6b) — the campaign controller.
 *
 * Owns the whole evaluate → arm-trigger → claim → render-state lifecycle described in
 * proposal.md "Campaign controller (widget, `src/`)". Deliberately framework-free (no React and no
 * redux imports) so the dwell-timer / debounce / readiness logic in here can be unit-tested with
 * fake timers and a mocked `CampaignApiClient`, independent of anything rendering — the approach
 * design.md's "Testing approach" section calls for. The render state it produces is pushed into
 * the redux store by the injected sink (`index.ts`), which is where `CampaignTeaser` reads it from
 * with `useSelector`, exactly like every other piece of widget state.
 *
 * One instance per widget session — see `index.ts` for the wired singleton.
 */
class CampaignController {
  private api: CampaignApiClient;

  private emitRenderState: RenderStateSink;

  private enabled = false;

  private pageContext: PageContext | null = null;

  /** Only the FIRST evaluate of a page load waits for the beacon; later ones must stay snappy. */
  private firstEvaluateDone = false;

  private waitForActivity: () => Promise<void>;


  /** The server's ORDERED candidate list, order preserved — see `attemptClaim`. */
  private candidates: EvaluationCandidate[] = [];

  /** campaignIds whose `delaySeconds` has elapsed in the current evaluate cycle. */
  private dwellElapsed = new Set<string>();

  /**
   * Whether an exit intent has been observed in the current cycle. Sticky, because the loader
   * grants exactly ONE exit intent per page/route: an intent that arrives while the evaluate call
   * is still in flight would otherwise be marked against an empty candidate list and the visitor's
   * single chance would be spent on nothing.
   */
  private exitIntentSeen = false;

  /** One claim per evaluate cycle — set the moment a claim is attempted, not when it resolves. */
  private claimAttempted = false;

  private evaluateDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  private lastEvaluateAt = -Infinity;

  private dwellTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Bumped at the start of every evaluate cycle. An in-flight evaluate or claim request from an
   * older cycle checks this before applying its result, so a slow response arriving after a
   * newer page context has already started a fresh cycle cannot overwrite it with stale data —
   * the concurrency hazard of two overlapping route changes racing their network calls.
   */
  private cycleId = 0;

  private renderState: CampaignRenderState = { status: 'idle' };

  /**
   * THE ENGAGEMENT — the campaign show the visitor is currently engaged with, and the single
   * answer both of its consumers read: `reportOutcome('replied')` for which show a reply belongs
   * to (REQ-CWC-05), and `startWidgetSession` for which show the session it creates is
   * attributed to (AC-4.3(b)).
   *
   * ONE rule governs its lifetime, and there is no second one anywhere:
   *
   *   An engagement BEGINS at `beginEngagement()` — the visitor revealed a greeting, by tapping
   *   the teaser or by having it auto-opened on them. It ENDS at `endEngagement()`, which is the
   *   ONLY thing that clears it, when the visitor is no longer in the conversation that greeting
   *   started. That is exactly three events:
   *
   *     1. the visitor DISMISSES that same show — they declined the greeting, so no
   *        conversation follows it and nothing later in the tab belongs to it;
   *     2. the conversation ENDS — `App`'s end-chat reset drops the session and mints a fresh
   *        visitor token, so every session after it belongs to a different visitor;
   *     3. campaigns are turned OFF (`setEnabled(false)`), the widget's own teardown.
   *
   *   A REPLY is deliberately NOT one of them. The reply is what the engagement is FOR: it
   *   starts the conversation, and the session that carries `campaignShowId` is created AFTER
   *   `reportOutcome('replied')` has already run. Reporting that reply at most once is
   *   `repliedReported`'s job, not the engagement's lifetime.
   *
   * Deliberately NOT derivable from `renderState`, which answers a different question — "what is
   * on screen" — and is written by the evaluate/claim cycle. A teaser merely DISPLAYED and never
   * touched sits there for the life of the page, and a route change can replace it with another
   * campaign's show; deciding either consumer from it stamps sessions and reports replies for
   * greetings nobody answered. `resetCycle()` correspondingly does not touch the engagement: a
   * visitor who reads a greeting, browses on, and only then replies is still engaged with it.
   *
   * Lives here rather than in `CampaignTeaser` because on mobile the reveal itself unmounts that
   * component: `WidgetLayout` gates the teaser on `!(isMobile && showChat)`, and the reveal sets
   * `showChat` in the same commit. The controller outlives every render.
   */
  private engagedShowId: string | null = null;

  /**
   * The engaged greeting's body while it waits for the pre-chat form to come down (AC-4.2).
   * Held here for the same reason `engagedShowId` is: the component that revealed it is gone by
   * the time the form's own teardown effect runs. `App` takes it once, after `dropMessages()`.
   * Engagement state, so it begins and ends with the engagement.
   */
  private pendingGreeting: string | null = null;

  /**
   * The visitor's own first words while they wait for the pre-chat form to come down.
   *
   * Parked for the SAME reason `pendingGreeting` is, and it is not optional: a reply typed in the
   * teaser cannot be sent in the commit that reveals it. `App`'s `ensureSession` reads
   * `isShowingForm` from the render that is being replaced, so a synchronous send skips session
   * creation entirely and posts to `POST /message` with an empty session id — the exact defect
   * AC-4.3(b)'s stamped-session funnel exists to prevent. On mobile the teaser is also unmounted
   * by that same commit, so nothing it owns could hold the text across it either.
   *
   * It also carries the case where the form is NOT skipped: the reply waits until the visitor has
   * completed the form, because that is the transition that creates the session it belongs to.
   * Engagement state, so it begins and ends with the engagement.
   */
  private pendingReply: string | null = null;

  /**
   * One `replied` report per engagement — `App` calls `reportOutcome` on every message, and a
   * reply does not end the engagement. Engagement state, so it begins and ends with it.
   */
  private repliedReported = false;

  /**
   * `waitForActivity` gates the FIRST evaluate on the visitor's first activity beacon. Injected
   * rather than imported so this controller stays free of the activity singleton and keeps the
   * fake-timer unit-testability its class docblock describes; it defaults to "do not wait", which
   * is the correct behaviour for a widget with no activity tracking wired in.
   */
  constructor(
    api: CampaignApiClient,
    emitRenderState: RenderStateSink,
    waitForActivity: () => Promise<void> = () => Promise.resolve(),
  ) {
    this.api = api;
    this.emitRenderState = emitRenderState;
    this.waitForActivity = waitForActivity;
  }

  getRenderState(): CampaignRenderState {
    return this.renderState;
  }

  /**
   * REQ-CWC-05 — event 0 of the lifetime on `engagedShowId`: the one moment the visitor engages
   * with a greeting, called by `CampaignTeaser` on the teaser click or the auto-open reveal.
   * `pendingGreeting` is the body when the campaign's `skipPreChatForm` took the form down and
   * the greeting has to wait for that teardown; `null` when it was added straight away.
   */
  beginEngagement(showId: string, pendingGreeting: string | null): void {
    this.engagedShowId = showId;
    this.pendingGreeting = pendingGreeting;
    this.pendingReply = null;
    this.repliedReported = false;
  }

  /**
   * The engaged show — see `engagedShowId` for the one rule that decides when this stops
   * answering. `null` means "attribute nothing, report nothing".
   */
  getEngagedShowId(): string | null {
    return this.engagedShowId;
  }

  /**
   * The ONLY thing that ends an engagement — see `engagedShowId` for the three events that call
   * it. Public because one of them is App's to observe: the end-chat reset drops the session and
   * mints a new visitor token, and an engagement that outlived it would stamp its show onto every
   * later session in the tab.
   */
  endEngagement(): void {
    this.engagedShowId = null;
    this.pendingGreeting = null;
    this.pendingReply = null;
    this.repliedReported = false;
  }

  /** Take-once: the greeting is added to the thread exactly one time. */
  takePendingGreeting(): string | null {
    const body = this.pendingGreeting;
    this.pendingGreeting = null;
    return body;
  }

  /** Hold the visitor's teaser reply until the form transition that owns its session lands. */
  parkPendingReply(text: string): void {
    this.pendingReply = text;
  }

  /** Take-once: the parked reply is sent exactly one time. */
  takePendingReply(): string | null {
    const text = this.pendingReply;
    this.pendingReply = null;
    return text;
  }

  /**
   * REQ-CWC-01 — gated on `GET /widget`'s `liveChatCampaigns.enabled`. Flipping to disabled tears
   * down every armed trigger and any surface currently shown, mirroring what a mid-session config
   * refresh or a rollback (feature param off) must do — REQ-CWC-04's degrade-to-"no campaign"
   * applies here too, not only to a bad network response.
   */
  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    if (!enabled) {
      this.resetCycle();
      // Event 3 of the engagement lifetime — see `engagedShowId`.
      this.endEngagement();
      this.setRenderState({ status: 'idle' });
      return;
    }
    // Order-independent: the loader can post IMBEE_PAGE_CONTEXT before GET /widget resolves.
    if (this.pageContext) this.scheduleEvaluate();
  }

  /** REQ-CWC-01 — fired by the widget's message listener on a trusted IMBEE_PAGE_CONTEXT. */
  onPageContext(pageContext: PageContext): void {
    this.pageContext = pageContext;
    if (!this.enabled) return;
    this.scheduleEvaluate();
  }

  /** REQ-CWC-03 — the exit-intent trigger (AC-2.6's `requiresExitIntent` candidates). */
  onExitIntent(): void {
    if (!this.enabled) return;
    this.exitIntentSeen = true;
    this.attemptClaim();
  }

  /**
   * REQ-CWC-05 — call once a greeting is delivered and the visitor either dismisses the teaser or
   * sends their first message. Both callers — `CampaignTeaser`'s dismiss handler and `App.tsx`'s
   * message-send hook — call it unconditionally rather than tracking "have I already reported"
   * themselves, so a call with nothing to report is a no-op. What "nothing to report" means
   * differs per outcome, and that difference is the whole of this method:
   *
   *  - `dismissed` — no surface on screen. Covers a suppressed claim, which never reaches a
   *    non-idle render state, and a second call after the first reset to idle.
   *  - `replied` — no engaged show. Covers a visitor who opened from the launcher under a teaser
   *    they ignored, and every message after the first.
   *
   * At most one outcome per show reaches the wire, by construction rather than by a flag per
   * route: any report leaves `renderState` idle, so no second `dismissed` can follow; a
   * `dismissed` of the engaged show ends the engagement, so no `replied` can follow; and
   * `repliedReported` covers the repeat calls App makes for the rest of the conversation.
   */
  reportOutcome(outcome: CampaignOutcome): void {
    const liveShowId = this.renderState.status === 'idle' ? null : this.renderState.showId;
    // WHICH show an outcome belongs to is not the same question for the two outcomes.
    // A DISMISSAL is always of the surface on screen — the visitor is looking at it as they
    // close it. A REPLY belongs to the show the visitor ENGAGED with, which `renderState` cannot
    // answer for the reasons `engagedShowId` documents: reading it here would report `replied`
    // for a teaser nobody touched, inflating `engaged` / `conversations` / `engagementRate` with
    // conversations no campaign started.
    const showId = outcome === 'replied' ? this.engagedShowId : liveShowId;
    // The surface is spent either way: a visitor who is now typing, or who just closed the
    // bubble, is not to be offered that same teaser again.
    if (liveShowId !== null) this.setRenderState({ status: 'idle' });
    // Event 1 of the engagement lifetime — see `engagedShowId`. Dismissing the engaged show is
    // the visitor declining it, so nothing after it belongs to that show: without this the id
    // stays attributable, and a later launcher-opened reply reports a contradictory `replied`
    // for a greeting the visitor closed AND stamps `campaignShowId` onto that session. The
    // service-side guard filters neither — a shown, dismissed, never-conversation-started show
    // matches every field it checks. A dismissal of some OTHER campaign's teaser is not this
    // engagement's business and leaves it alone.
    if (outcome === 'dismissed' && showId !== null && showId === this.engagedShowId) this.endEngagement();
    if (showId === null) return;
    // App calls this on EVERY message send, so only the first is this show's outcome.
    if (outcome === 'replied') {
      if (this.repliedReported) return;
      this.repliedReported = true;
    }
    this.api.reportOutcome(showId, outcome).catch((error) => {
      // Best-effort for what the VISITOR sees — an outcome the server never records does not
      // change the UI, and retrying would need a second showId-scoped state machine for a
      // non-critical metric. It is NOT best-effort for the analytics it feeds (AC-4.4/AC-7.1), so
      // it is reported rather than swallowed.
      campaignWarn('outcome.reportFailed', { showId, outcome, message: (error as Error)?.message });
    });
  }

  private scheduleEvaluate(): void {
    const now = Date.now();
    const elapsed = now - this.lastEvaluateAt;
    if (elapsed >= EVALUATE_MIN_INTERVAL_MS) {
      void this.runEvaluate();
      return;
    }
    if (this.evaluateDebounceTimer) return; // a trailing call is already scheduled
    const wait = EVALUATE_MIN_INTERVAL_MS - elapsed;
    this.evaluateDebounceTimer = setTimeout(() => {
      this.evaluateDebounceTimer = null;
      void this.runEvaluate();
    }, wait);
  }

  private async runEvaluate(): Promise<void> {
    // Stamped BEFORE the activity gate below, not after: `scheduleEvaluate` measures the debounce
    // window from this value, so awaiting first would leave it stale for the duration of the wait
    // and let a second evaluate through the one-per-second limit (REQ-CWC-01).
    this.lastEvaluateAt = Date.now();

    /**
     * A dwell / page-count condition cannot match a visitor whose activity record does not exist
     * yet. The widget fires the first beacon and the first evaluate off the SAME page-context
     * message, and evaluate always won the race — so a genuinely-new visitor was evaluated against
     * an empty record, matched nothing, and saw the teaser only after a RELOAD, which a real
     * visitor never performs. Awaiting the first beacon closes that. It resolves as soon as the
     * beacon is sent, immediately when tracking is off, or after a short timeout — so a broken
     * ingest DELAYS the greeting rather than suppressing it.
     */
    if (!this.firstEvaluateDone) {
      this.firstEvaluateDone = true;
      await this.waitForActivity();
    }

    this.resetCycle();
    const cycleId = ++this.cycleId;
    const pageContext = this.pageContext;
    if (!this.enabled || !pageContext) return;

    let raw: unknown = null;
    try {
      raw = await this.api.evaluate({ pageContext });
    } catch (error) {
      campaignWarn('evaluate.requestFailed', { url: pageContext.url, message: (error as Error)?.message });
      raw = null;
    }
    // A newer cycle (another route change) may have started while this request was in flight.
    if (cycleId !== this.cycleId || !this.enabled) return;

    const { candidates } = parseEvaluateResponse(raw);
    this.candidates = candidates;
    campaignDebug('evaluate.candidates', { url: pageContext.url, count: candidates.length });
    // AC-1.3 — "`0` means evaluate immediately", so a zero-delay candidate is ready as soon as the
    // list lands rather than one timer tick later.
    candidates.filter((c) => c.delaySeconds === 0).forEach((c) => this.dwellElapsed.add(c.campaignId));
    // An exit intent may already be waiting: it can arrive while the request is in flight, and the
    // loader grants only one per page/route, so a lost one is a spent chance.
    this.attemptClaim();
    if (!this.claimAttempted) this.armDwellTimer();
  }

  /**
   * Arms a SINGLE timer at the soonest not-yet-elapsed `delaySeconds`, and on fire marks EVERY
   * candidate at or below that delay elapsed TOGETHER before attempting a claim. Two candidates
   * with the same delay must both be in the ready set before the server-ordered pick runs, rather
   * than whichever one's own `setTimeout` happened to fire first winning by scheduling accident.
   * Re-arms for the next distinct delay, so a later candidate still becomes ready when an earlier
   * one is still waiting on an exit intent that never comes.
   */
  private armDwellTimer(): void {
    this.clearDwellTimer();
    const pending = this.candidates.filter((c) => !this.dwellElapsed.has(c.campaignId));
    if (!pending.length) return;

    const nextDelaySeconds = Math.min(...pending.map((c) => c.delaySeconds));
    const elapsedSoFar = this.dwellElapsed.size
      ? Math.max(...this.candidates.filter((c) => this.dwellElapsed.has(c.campaignId)).map((c) => c.delaySeconds))
      : 0;
    this.dwellTimer = setTimeout(() => {
      this.dwellTimer = null;
      pending.filter((c) => c.delaySeconds <= nextDelaySeconds).forEach((c) => this.dwellElapsed.add(c.campaignId));
      this.attemptClaim();
      if (!this.claimAttempted) this.armDwellTimer();
    }, Math.max(0, nextDelaySeconds - elapsedSoFar) * 1000);
  }

  /**
   * AC-2.6 — `requiresExitIntent` arms the exit-intent listener "instead of, or in addition to,
   * the dwell timer", so a candidate that needs both is ready only once both have happened.
   */
  private isReady(candidate: EvaluationCandidate): boolean {
    if (!this.dwellElapsed.has(candidate.campaignId)) return false;
    return candidate.requiresExitIntent ? this.exitIntentSeen : true;
  }

  /** AC-1.3 — `immediate` is the zero-delay case, and is NOT the same wire value as `dwell`. */
  private static triggerFor(candidate: EvaluationCandidate): CampaignTrigger {
    if (candidate.requiresExitIntent) return 'exitIntent';
    return candidate.delaySeconds === 0 ? 'immediate' : 'dwell';
  }

  /**
   * REQ-CWC-03. The winner is simply the FIRST ready candidate in the server's own order: the
   * api-document states the list is "ordered by priority descending, then oldest first — the
   * AC-1.6 tie-break, resolved server-side so the client never has to know it". Every other ready
   * candidate rides along as `alsoReadyCampaignIds` so the server can write their AC-5.4
   * `suppressedPriority` records; re-deriving the rule here would be a second, divergent copy of
   * a rule that has one owner.
   */
  private attemptClaim(): void {
    if (this.claimAttempted || !this.enabled) return;
    const readyCandidates = this.candidates.filter((c) => this.isReady(c));
    if (!readyCandidates.length) return;

    const [top, ...alsoReady] = readyCandidates;
    this.claimAttempted = true;
    this.clearDwellTimer();
    void this.performClaim(
      top,
      alsoReady.map((c) => c.campaignId),
      this.cycleId,
    );
  }

  private async performClaim(candidate: EvaluationCandidate, alsoReadyCampaignIds: string[], cycleId: number): Promise<void> {
    const pageContext = this.pageContext;
    if (!pageContext) return;
    const triggeredBy = CampaignController.triggerFor(candidate);
    let raw: unknown = null;
    try {
      raw = await this.api.claim({
        campaignId: candidate.campaignId,
        pageUrl: pageContext.url,
        triggeredBy,
        alsoReadyCampaignIds,
      });
    } catch (error) {
      campaignWarn('claim.requestFailed', {
        campaignId: candidate.campaignId,
        triggeredBy,
        message: (error as Error)?.message,
      });
      raw = null;
    }
    if (cycleId !== this.cycleId || !this.enabled) return;

    const parsed = parseClaimResponse(raw);
    if (parsed.mode === 'live') {
      campaignDebug('claim.live', { showId: parsed.showId, entryStyle: parsed.entryStyle, triggeredBy });
      this.setRenderState({
        status: parsed.entryStyle,
        showId: parsed.showId,
        greeting: parsed.greeting,
        skipPreChatForm: parsed.skipPreChatForm,
      });
    } else {
      campaignDebug('claim.suppressed', { campaignId: candidate.campaignId, reason: parsed.reason, triggeredBy });
      this.setRenderState({ status: 'idle' });
    }
  }

  /** Cycle state ONLY — the engagement is not cycle state; see `engagedShowId`. */
  private resetCycle(): void {
    this.candidates = [];
    this.dwellElapsed.clear();
    this.exitIntentSeen = false;
    this.claimAttempted = false;
    this.clearDwellTimer();
    if (this.evaluateDebounceTimer) {
      clearTimeout(this.evaluateDebounceTimer);
      this.evaluateDebounceTimer = null;
    }
  }

  private clearDwellTimer(): void {
    if (this.dwellTimer) {
      clearTimeout(this.dwellTimer);
      this.dwellTimer = null;
    }
  }

  private setRenderState(next: CampaignRenderState): void {
    this.renderState = next;
    this.emitRenderState(next);
  }
}

export default CampaignController;
